import React from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { MapPin, Clock, Baby, ChevronLeft, CheckCircle2, Info, CalendarClock } from "lucide-react";
import { PillButton } from "../../components/ui/PillButton";
import { StickerCard } from "../../components/ui/StickerCard";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { useApp } from "../../context/AppContext";

export default function ActivityDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getActivity, activityStatus, slotRemaining } = useApp();
  const activity = getActivity(slug);

  if (!activity) return <Navigate to="/activities" replace />;
  const status = activityStatus(activity);

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <button onClick={() => navigate("/activities")} className="flex items-center gap-1 text-sea-deep/60 font-bold mb-4" data-testid="detail-back">
        <ChevronLeft className="w-4 h-4" /> Semua Aktivitas
      </button>

      <StickerCard className="overflow-hidden">
        <div className="relative h-56 sm:h-72">
          <img src={activity.image} alt={activity.name} className="w-full h-full object-cover" />
          <div className="absolute top-4 left-4"><StatusBadge status={status} /></div>
          <div className="absolute -bottom-8 left-6 w-20 h-20 rounded-2xl bg-white shadow-playful p-2.5 flex items-center justify-center">
            <img src={activity.icon} alt="" className="max-w-full max-h-full object-contain" />
          </div>
        </div>
        <div className="p-6 pt-12">
          <h1 className="font-display text-3xl sm:text-4xl text-sea-deep">{activity.name}</h1>
          <p className="mt-2 text-sea-deep/75 font-semibold">{activity.description}</p>

          <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Info3 icon={<Baby className="w-4 h-4" />} label="Umur" value={activity.ageRecommendation} />
            <Info3 icon={<Clock className="w-4 h-4" />} label="Durasi" value={activity.duration} />
            <Info3 icon={<MapPin className="w-4 h-4" />} label="Lokasi" value={activity.location} />
          </div>

          {activity.rules?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-display text-lg text-sea-deep flex items-center gap-2"><Info className="w-5 h-5 text-coral" /> Aturan</h3>
              <ul className="mt-2 space-y-1">
                {activity.rules.map((r) => (
                  <li key={r} className="flex items-start gap-2 text-sm font-semibold text-sea-deep/75">
                    <CheckCircle2 className="w-4 h-4 text-grass mt-0.5 shrink-0" /> {r}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Slots */}
          {activity.bookingRequired && (
            <div className="mt-6">
              <h3 className="font-display text-lg text-sea-deep flex items-center gap-2"><CalendarClock className="w-5 h-5 text-coral" /> Slot Waktu Tersedia</h3>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {activity.slots.map((s) => {
                  const rem = slotRemaining(activity.id, s.id);
                  return (
                    <div key={s.id} className={`rounded-2xl border-2 p-3 text-center ${rem > 0 ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-70"}`}>
                      <p className="font-display text-sea-deep">{s.start}–{s.end}</p>
                      <p className={`text-xs font-bold mt-1 ${rem > 0 ? "text-grass" : "text-destructive"}`}>{rem > 0 ? `${rem} slot` : "Penuh"}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-8">
            {!activity.bookingRequired ? (
              <PillButton size="lg" variant="sky" disabled className="w-full sm:w-auto" data-testid="detail-no-reservation">
                Tanpa Reservasi — Datang Aja!
              </PillButton>
            ) : status === "fully-booked" ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <PillButton size="lg" variant="destructive" disabled data-testid="detail-fully-booked">Fully Booked</PillButton>
                <PillButton size="lg" variant="white" data-testid="detail-waiting-list">Join Waiting List</PillButton>
              </div>
            ) : (
              <PillButton
                size="lg"
                className="w-full sm:w-auto"
                onClick={() => navigate("/book-activity", { state: { activitySlug: activity.slug } })}
                data-testid="detail-book"
              >
                Book This Activity
              </PillButton>
            )}
          </div>
        </div>
      </StickerCard>
    </div>
  );
}

const Info3 = ({ icon, label, value }) => (
  <div className="bg-sky/15 rounded-2xl p-3">
    <p className="text-xs uppercase font-bold text-sea-deep/50 flex items-center gap-1">{icon}{label}</p>
    <p className="mt-1 font-display text-sea-deep text-sm">{value}</p>
  </div>
);
